import { openContactEmail, openContactCall } from "../utils/contactCommunication";
import useProfileTab from "../hooks/useProfileTab";
import { CandidateRecruitingActions } from "./RecruitingNextActions";
// src/components/CandidateTabsLayout.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Link } from "@mui/material";

import Breadcrumb from "./Breadcrumb";
import StatsSlider from "./StatsSlider";
import CompanyProfileHeader from "./CompanyProfileHeader";
import CompanyHeader from "./LeadTabsContent/DetailSections/CompanyHeader";
import ListTableTabsTemplate from "./layouts/ListTableTabsTemplate";

import QuickActionsCard from "./sidebar-widgets/QuickActionsCard";
import AIAgentCard from "./sidebar-widgets/AIAgentCard";
import ActivitiesCard from "./sidebar-widgets/ActivitiesCard";
import HiringTeamCard from "./sidebar-widgets/HiringTeamCard";
import StagesCard from "./sidebar-widgets/StagesCard";
import JobMatchesCard from "./sidebar-widgets/JobMatchesCard";

import CandidateStagesTabContent from "./CandidatesTabsContent/CandidateStagesTabContent";
import CandidateActivitiesTabContent from "./CandidatesTabsContent/CandidateActivitiesTabContent";
import CandidateDetailsTabContent from "./CandidatesTabsContent/CandidateDetailsTabContent";
import CandidateShareDialog from "./CandidateShareDialog";
import { openInterviewScheduler } from "../utils/interviewScheduling";
import AddCandidateToJobDialog from "./PopupWindows/AddCandidateToJobDialog";
import { TAB_VIEW_CONTENT_WRAPPER_SX, TAB_VIEW_HEADER_SX } from "./tabViewContentSpacing";
import { toCandidateActivityItem } from "../utils/entityActivities";
import {
  formatEntityAddedDateText,
  resolveEntityAddedDate,
} from "../utils/entityDateText";
import { toTimeValue } from "../utils/dateTime";
import { Call, Sms, Calendar, Briefcase } from "iconsax-react";
import { IosShareOutlined as IosShareOutlinedIcon } from "./icons/FigmaControlIcons";
import { MatchActionIcon } from "./ActionButtonIcons";
import { findTabLabel } from "../utils/breadcrumbs";
import { CANDIDATE_STATUS_OPTIONS } from "../utils/candidateStatusOptions";
import { fetchRecruiterUsers, normalizeTeamMemberName } from "../utils/teamMembers";

const BORDER = "#E5E7EB";
const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const DEFAULT_CANDIDATE_STATUS = "New";
const JOB_CANDIDATE_STAGE_OPTIONS = [
  "New",
  "Applied",
  "Matched",
  "Sourced",
  "Contacted",
  "Screened",
  "Presented",
  "Interview",
  "Assessment",
  "Offer",
  "Hired",
  "Rejected",
];

function getCandidateStatusLabel(candidateObj = null) {
  const safe = candidateObj && typeof candidateObj === "object" ? candidateObj : {};
  return (
    String(safe.status || safe.candidate_status || safe.candidateStatus || "").trim() ||
    DEFAULT_CANDIDATE_STATUS
  );
}

const normalizeTab = (t) => {
  const v = String(t || "").toLowerCase();
  const ok = ["details", "stages", "activities"];
  return ok.includes(v) ? v : "details";
};

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("jwt") ||
  localStorage.getItem("accessToken") ||
  "";

const toFiniteCount = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatSidebarStageDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const normalizeCountableArray = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean);
      }
    } catch {
      // Fall back to tokenizing a plain string.
    }

    return trimmed
      .split(/\s*(?:,|\|)\s*/g)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

function buildSidebarActivityLineCandidates(item) {
  const metaLines = Array.isArray(item?.meta)
    ? item.meta
        .map((entry) =>
          String([entry?.label || "", entry?.value || ""].filter(Boolean).join(" "))
            .replace(/\s+/g, " ")
            .trim()
        )
        .filter(Boolean)
    : [];

  const bodyLines = String(item?.body || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  const title = String(item?.title || "")
    .replace(/\s+/g, " ")
    .trim();

  const lines = [];

  [...bodyLines, ...metaLines].forEach((line) => {
    if (!line || lines.includes(line)) return;
    lines.push(line);
  });

  if (!lines.length && title) {
    lines.push(title);
  }

  if (lines.length < 2 && title && !lines.includes(title)) {
    lines.push(title);
  }

  return lines;
}

export default function CandidateTabsLayout({
  candidate,
  detailFieldOptions,
  initialTab = "details",
  onBack,
}) {
  const navigate = useNavigate();

  const [tab, setTab] = useProfileTab(initialTab, normalizeTab);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [candidateLive, setCandidateLive] = useState(candidate || null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [stageRefreshKey, setStageRefreshKey] = useState(0);
  const [stageTabCount, setStageTabCount] = useState(null);
  const [sidebarStageRows, setSidebarStageRows] = useState([]);
  const [sidebarStageFilter, setSidebarStageFilter] = useState("all");
  const [sidebarStagePage, setSidebarStagePage] = useState(0);
  const [sidebarStagesLoading, setSidebarStagesLoading] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [addToJobDialogOpen, setAddToJobDialogOpen] = useState(false);
  const [recruiterUsers, setRecruiterUsers] = useState([]);
  const [statusToast, setStatusToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  useEffect(() => {
    setCandidateLive((previous) => {
      const previousId = previous?.candidate_id || previous?.id;
      const nextId = candidate?.candidate_id || candidate?.id;
      // A refreshed table can supply an older object for the same candidate.
      // Preserve the live record so new activities remain visible.
      if (previousId && nextId && String(previousId) === String(nextId)) return previous;
      return candidate || null;
    });
  // The prop object is intentionally excluded: list refreshes recreate it with
  // older data while this view owns the current candidate detail state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.candidate_id, candidate?.id]);

  useEffect(() => {
    setSelectedStatus(getCandidateStatusLabel(candidateLive || candidate));
  }, [candidateLive, candidate]);

  useEffect(() => {
    let active = true;

    fetchRecruiterUsers({ apiBase: API_BASE })
      .then((rows) => {
        if (!active) return;
        setRecruiterUsers(Array.isArray(rows) ? rows : []);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Failed to load recruiter users for candidate view:", error);
      });

    return () => {
      active = false;
    };
  }, []);

  const candidateDetailRequest = useRef(0);
  const refreshCandidateDetails = useCallback(
    async (overrideCandidateId = null, options = {}) => {
      const resolvedCandidateId =
        overrideCandidateId || candidate?.candidate_id || candidate?.id || null;
      if (!resolvedCandidateId) return null;

      const requestId = ++candidateDetailRequest.current;
      const token = getToken();
      const response = await fetch(
        `${API_BASE}/api/candidates/${encodeURIComponent(resolvedCandidateId)}`,
        {
          signal: options?.signal,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(token ? { "x-access-token": token } : {}),
          },
        }
      );

      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(message || `Failed to fetch candidate ${resolvedCandidateId}`);
      }

      const payload = await response.json().catch(() => ({}));
      const nextCandidate = payload?.candidate || payload || null;

      if (requestId !== candidateDetailRequest.current || options?.signal?.aborted) return null;
      if (nextCandidate && typeof nextCandidate === "object") {
        setCandidateLive((prev) => ({ ...(prev || {}), ...nextCandidate }));
      }

      return nextCandidate;
    },
    [candidate?.candidate_id, candidate?.id]
  );

  const refreshCandidateStageCount = useCallback(
    async (overrideCandidateId = null, options = {}) => {
      const resolvedCandidateId =
        overrideCandidateId || candidate?.candidate_id || candidate?.id || null;
      if (!resolvedCandidateId) {
        setStageTabCount(null);
        return null;
      }

      const token = getToken();
      const response = await fetch(
        `${API_BASE}/api/candidate-stages/${encodeURIComponent(
          resolvedCandidateId
        )}?stage=all&sortBy=default`,
        {
          signal: options?.signal,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(token ? { "x-access-token": token } : {}),
          },
        }
      );

      if (!response.ok) {
        const message = await response.text().catch(() => "");
        throw new Error(message || `Failed to fetch candidate stages for ${resolvedCandidateId}`);
      }

      const payload = await response.json().catch(() => ({}));
      const totalCount = Math.max(
        0,
        toFiniteCount(
          payload?.total ??
            (Array.isArray(payload?.stages)
              ? payload.stages.reduce(
                  (sum, stage) => sum + Math.max(0, toFiniteCount(stage?.count)),
                  0
                )
              : 0)
        )
      );

      setStageTabCount(totalCount);
      return totalCount;
    },
    [candidate?.candidate_id, candidate?.id]
  );

  const refreshSidebarStageRows = useCallback(async () => {
    const resolvedCandidateId = candidate?.candidate_id || candidate?.id || null;
    if (!resolvedCandidateId) {
      setSidebarStageRows([]);
      return;
    }

    setSidebarStagesLoading(true);
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE}/api/candidate-stages/${encodeURIComponent(resolvedCandidateId)}?stage=all&sortBy=default`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(token ? { "x-access-token": token } : {}),
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch candidate stages.");
      const payload = await response.json().catch(() => ({}));
      setSidebarStageRows(
        Array.isArray(payload?.stages)
          ? payload.stages.flatMap((group) => (Array.isArray(group?.items) ? group.items : []))
          : []
      );
    } catch (error) {
      console.error("Failed to refresh candidate sidebar stages:", error);
      setSidebarStageRows([]);
    } finally {
      setSidebarStagesLoading(false);
    }
  }, [candidate?.candidate_id, candidate?.id]);

  useEffect(() => {
    const candidateId = candidate?.candidate_id || candidate?.id;
    if (!candidateId) return undefined;

    const controller = new AbortController();

    refreshCandidateDetails(candidateId, { signal: controller.signal }).catch((err) => {
      if (err?.name === "AbortError") return;
      console.error("Failed to refresh candidate activities:", err);
    });
    const refresh = () => {
      setStageRefreshKey(value => value + 1);
      refreshCandidateDetails(candidateId, { signal: controller.signal }).catch(() => {});
    };
    window.addEventListener("rpo:activities-refresh", refresh);

    return () => {
      controller.abort();
      window.removeEventListener("rpo:activities-refresh", refresh);
    };
  }, [candidate?.candidate_id, candidate?.id, refreshCandidateDetails]);

  useEffect(() => {
    const candidateId = candidate?.candidate_id || candidate?.id;
    if (!candidateId) {
      setStageTabCount(null);
      return undefined;
    }

    const controller = new AbortController();

    refreshCandidateStageCount(candidateId, { signal: controller.signal }).catch((err) => {
      if (err?.name === "AbortError") return;
      console.error("Failed to refresh candidate stage count:", err);
      setStageTabCount(null);
    });

    return () => {
      controller.abort();
    };
  }, [
    candidate?.candidate_id,
    candidate?.id,
    refreshCandidateStageCount,
    stageRefreshKey,
  ]);

  useEffect(() => {
    void refreshSidebarStageRows();
  }, [refreshSidebarStageRows, stageRefreshKey]);

  const dateText = useMemo(
    () => formatEntityAddedDateText(resolveEntityAddedDate(candidateLive, candidate)),
    [candidateLive, candidate]
  );

  const activeCandidate = candidateLive || candidate;
  const candidateId = activeCandidate?.candidate_id || activeCandidate?.id;
  const candidateName =
    activeCandidate?.full_name || activeCandidate?.name || "Candidate";
  const recruiter =
    activeCandidate?.owner || activeCandidate?.recruiter_name || "Stan Smith";
  const recruiterUsersByName = useMemo(() => {
    const nextMap = new Map();

    (Array.isArray(recruiterUsers) ? recruiterUsers : []).forEach((user) => {
      const lookupKey = normalizeTeamMemberName(user?.name).toLowerCase();
      if (!lookupKey || nextMap.has(lookupKey)) return;
      nextMap.set(lookupKey, user);
    });

    return nextMap;
  }, [recruiterUsers]);
  const recruiterProfileId = useMemo(() => {
    const lookupKey = normalizeTeamMemberName(recruiter).toLowerCase();
    if (!lookupKey) return null;

    const matchedRecruiter = recruiterUsersByName.get(lookupKey);
    const numericId = Number(matchedRecruiter?.id || 0);
    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }, [recruiter, recruiterUsersByName]);
  const recruiterMetaValue = useMemo(() => {
    if (!recruiterProfileId) return recruiter;

    return (
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={() => navigate(`/profile/${encodeURIComponent(String(recruiterProfileId))}`)}
        sx={{
          p: 0,
          border: 0,
          backgroundColor: "transparent",
          color: "inherit",
          fontSize: "inherit",
          fontWeight: "inherit",
          fontFamily: "inherit",
          cursor: "pointer",
          textUnderlineOffset: "2px",
        }}
      >
        {recruiter}
      </Link>
    );
  }, [navigate, recruiter, recruiterProfileId]);
  const aiCompanySeed = useMemo(() => {
    const companyName = String(
      activeCandidate?.company_name ||
        activeCandidate?.company ||
        activeCandidate?.current_company ||
        activeCandidate?.currentCompany ||
        activeCandidate?.employer ||
        activeCandidate?.latest_company ||
        ""
    ).trim();
    const website = String(
      activeCandidate?.company_website ||
        activeCandidate?.website ||
        activeCandidate?.current_company_website ||
        activeCandidate?.employer_website ||
        ""
    ).trim();
    const careerSite = String(
      activeCandidate?.career_site ||
        activeCandidate?.career_website ||
        activeCandidate?.company_career_website ||
        ""
    ).trim();
    const companyLink = String(
      activeCandidate?.company_link ||
        website ||
        careerSite ||
        activeCandidate?.linkedin_company_url ||
        ""
    ).trim();

    return {
      name: companyName,
      website,
      career_site: careerSite,
      company_link: companyLink,
      created_at: resolveEntityAddedDate(activeCandidate),
      updated_at: String(
        activeCandidate?.updated_at ||
          activeCandidate?.updatedAt ||
          activeCandidate?.last_activity_at ||
          activeCandidate?.lastActivityAt ||
          ""
      ).trim(),
      notes: companyName
        ? `Create or refresh ${companyName}, scan the public website for company information, leadership, team members, and current openings.`
        : "",
    };
  }, [activeCandidate]);

  const handleStatusChange = useCallback(
    async (nextValue) => {
      const selectedValue = String(nextValue || "").trim();
      const selectedOption = CANDIDATE_STATUS_OPTIONS.find(
        (option) => option.value === selectedValue
      );

      if (!selectedOption) {
        setSelectedStatus(getCandidateStatusLabel(activeCandidate));
        return;
      }

      if (!candidateId || statusSaving) {
        setSelectedStatus(selectedValue);
        return;
      }

      const previousValue = selectedStatus;
      const previousLabel = getCandidateStatusLabel(activeCandidate);
      const token = getToken();

      setSelectedStatus(selectedValue);
      setStatusSaving(true);
      setCandidateLive((prev) => ({
        ...(prev || {}),
        status: selectedOption.label,
        candidate_status: selectedOption.label,
      }));

      try {
        const response = await fetch(
          `${API_BASE}/api/candidates/${encodeURIComponent(candidateId)}`,
          {
            method: "PUT",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(token ? { "x-access-token": token } : {}),
            },
            body: JSON.stringify({
              id: candidateId,
              candidate_id: candidateId,
              status: selectedOption.label,
              candidate_status: selectedOption.label,
            }),
          }
        );

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to update candidate status (${response.status})`);
        }

        const nextCandidate = payload?.candidate;
        const savedStatus =
          String(
            payload?.status ||
              nextCandidate?.status ||
              nextCandidate?.candidate_status ||
              selectedOption.label
          ).trim() ||
          selectedOption.label;

        setCandidateLive((prev) => ({
          ...(prev || {}),
          ...(nextCandidate && typeof nextCandidate === "object" ? nextCandidate : {}),
          status: savedStatus,
          candidate_status: savedStatus,
        }));
        setSelectedStatus(savedStatus);

        // The status endpoint can create a timeline entry. Refresh the full
        // candidate record so a mounted Activities tab receives it immediately.
        await refreshCandidateDetails(candidateId).catch((error) => {
          console.error("Failed to refresh candidate after status update:", error);
        });
        setStatusToast({
          open: true,
          severity: "success",
          message: `Candidate status updated to "${savedStatus}".`,
        });
      } catch (error) {
        setSelectedStatus(previousValue);
        setCandidateLive((prev) => ({
          ...(prev || {}),
          status: previousLabel,
          candidate_status: previousLabel,
        }));
        console.error("Failed to update candidate status:", error);
        setStatusToast({
          open: true,
          severity: "error",
          message: error?.message || "Failed to update candidate status.",
        });
      } finally {
        setStatusSaving(false);
      }
    },
    [
      activeCandidate,
      candidateId,
      refreshCandidateDetails,
      selectedStatus,
      statusSaving,
    ]
  );

  const activityItems = useMemo(() => {
    return (Array.isArray(activeCandidate?.activities)
      ? activeCandidate.activities
      : []
    )
      .map((activity) =>
        toCandidateActivityItem({
          ...activity,
          // Older candidate activity records can omit their own timestamp.
          // Use the candidate creation timestamp for the generated audit entry.
          created_at:
            activity?.created_at ||
            activity?.createdAt ||
            (String(activity?.title || "").trim().toLowerCase() === "candidate created"
              ? resolveEntityAddedDate(activeCandidate)
              : ""),
        })
      )
      .filter(Boolean);
  }, [activeCandidate]);

  const runLoggedQuickAction = useCallback(
    (_actionKey, _actionLabel, actionFn) => () => {
      actionFn?.();
    },
    []
  );

  const handleQuickActionAddToJob = useCallback(() => {
    setAddToJobDialogOpen(true);
  }, []);

  const handleQuickActionAddCandidateToJob = useCallback(
    async (selectedJob = {}, callbackContext = {}) => {
      const numericCandidateId = Number(candidateId || 0);
      const numericJobId = Number(selectedJob?.id || selectedJob?.job_id || 0);
      const selectedStage = String(
        callbackContext?.candidateStage || JOB_CANDIDATE_STAGE_OPTIONS[0] || "New"
      ).trim();

      if (!Number.isFinite(numericCandidateId) || numericCandidateId <= 0) {
        throw new Error("Missing candidate id.");
      }

      if (!Number.isFinite(numericJobId) || numericJobId <= 0) {
        throw new Error("Missing job id.");
      }

      const token = getToken();
      const response = await fetch(
        `${API_BASE}/api/jobs/${encodeURIComponent(numericJobId)}/matches`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(token ? { "x-access-token": token } : {}),
          },
          body: JSON.stringify({
            candidate_id: numericCandidateId,
            stage: selectedStage,
          }),
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload?.error || `Failed to add candidate to job (${response.status})`
        );
      }

      await refreshCandidateDetails(numericCandidateId).catch((error) => {
        console.error("Failed to refresh candidate after adding to a job:", error);
      });

      setStageRefreshKey((prev) => prev + 1);
      setTab("stages");

      const jobLabel =
        [
          String(selectedJob?.job_title || selectedJob?.title || "").trim(),
          String(selectedJob?.company_name || selectedJob?.company || "").trim(),
        ]
          .filter(Boolean)
          .join(" at ") || "the job";

      setStatusToast({
        open: true,
        severity: "success",
        message:
          payload?.created === false
            ? `${candidateName} is already linked to ${jobLabel}. Stage set to ${selectedStage}.`
            : `${candidateName} added to ${jobLabel} in ${selectedStage}.`,
      });
    },
    [candidateId, candidateName, refreshCandidateDetails, setTab]
  );

  const handleCandidateStageDataChanged = useCallback(async () => {
    setStageRefreshKey((prev) => prev + 1);
    await refreshCandidateDetails(candidateId).catch((error) => {
      console.error("Failed to refresh candidate after stage update:", error);
    });
  }, [candidateId, refreshCandidateDetails]);

  const sidebarActivityItems = useMemo(() => {
    return [...activityItems]
      .sort((left, right) => {
        const leftTime = toTimeValue(left?.ts);
        const rightTime = toTimeValue(right?.ts);
        return rightTime - leftTime;
      })
      .slice(0, 3)
      .map((item, index) => {
        const lines = buildSidebarActivityLineCandidates(item);

        return {
          id: item?.id || `candidate-sidebar-activity-${index}`,
          dateText: item?.dateText || "",
          messageLine1: lines[0] || item?.title || "Activity",
          messageLine2: lines[1] || "",
          showActions: item?.type !== "note",
        };
      });
  }, [activityItems]);

  const stats = useMemo(() => {
    const ownersCount = normalizeCountableArray(
      activeCandidate?.owners ??
        activeCandidate?.owner_names ??
        activeCandidate?.team_members ??
        activeCandidate?.team_member ??
        activeCandidate?.owner_name ??
        activeCandidate?.owner
    ).length;
    const activitiesCount = Math.max(
      toFiniteCount(activeCandidate?.activitiesCount ?? activeCandidate?.activities_count),
      activityItems.length
    );
    const skillsCount = normalizeCountableArray(
      activeCandidate?.skills ?? activeCandidate?.skills_text ?? activeCandidate?.skills_json
    ).length;
    const experienceCount = normalizeCountableArray(
      activeCandidate?.experience_items ?? activeCandidate?.experience
    ).length;
    const educationCount = normalizeCountableArray(
      activeCandidate?.education_items ?? activeCandidate?.education
    ).length;
    const documentsCount = Math.max(
      normalizeCountableArray(
        activeCandidate?.documents ?? activeCandidate?.documents_json ?? activeCandidate?.documentsJson
      ).length,
      activeCandidate?.has_resume ||
        activeCandidate?.resume_url ||
        activeCandidate?.resume ||
        activeCandidate?.resumeUrl
        ? 1
        : 0
    );

    return [
      { id: "owners", value: String(ownersCount), label: "Owners" },
      { id: "activities", value: String(activitiesCount), label: "Activities" },
      { id: "skills", value: String(skillsCount), label: "Skills" },
      { id: "experience", value: String(experienceCount), label: "Experience" },
      { id: "education", value: String(educationCount), label: "Education" },
      { id: "documents", value: String(documentsCount), label: "Documents" },
    ];
  }, [activeCandidate, activityItems.length]);

  const tabCounts = useMemo(
    () => ({
      stages: stageTabCount,
      activities: Math.max(
        toFiniteCount(activeCandidate?.activitiesCount ?? activeCandidate?.activities_count),
        activityItems.length
      ),
    }),
    [
      activeCandidate?.activitiesCount,
      activeCandidate?.activities_count,
      activityItems.length,
      stageTabCount,
    ]
  );

  const handleStatItemClick = useCallback((item) => {
    const statId = String(item?.id || "");
    if (statId === "activities") setTab("activities");
    else if (["owners", "skills", "experience", "education", "documents"].includes(statId)) {
      setTab("details");
    }
  }, [setTab]);

  const handleDeleteActivity = useCallback(
    async (item) => {
      const activityId = Number(item?.id || 0);
      if (!Number.isFinite(activityId) || activityId <= 0) return;

      const confirmed = window.confirm("Delete this activity? This cannot be undone.");
      if (!confirmed) return;

      const token = getToken();

      try {
        const response = await fetch(
          `${API_BASE}/api/activities/${encodeURIComponent(activityId)}`,
          {
            method: "DELETE",
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(token ? { "x-access-token": token } : {}),
            },
          }
        );

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to delete activity (${response.status})`);
        }

        setCandidateLive((prev) => {
          if (!prev) return prev;

          const previousActivities = Array.isArray(prev.activities) ? prev.activities : [];
          const nextActivities = previousActivities.filter(
            (activity) => Number(activity?.id) !== activityId
          );

          return {
            ...prev,
            activities: nextActivities,
            activitiesCount: nextActivities.length,
            activities_count: nextActivities.length,
          };
        });
      } catch (error) {
        console.error("Failed to delete candidate activity:", error);
        window.alert(error?.message || "Failed to delete activity.");
      }
    },
    []
  );

  const headerTabs = useMemo(
    () => [
      { value: "details", label: "Details" },
      { value: "stages", label: "Stages", countKey: "stages" },
      { value: "activities", label: "Activities", countKey: "activities" },
    ],
    []
  );
  const activeBreadcrumbLabel = useMemo(
    () => findTabLabel(headerTabs, tab),
    [headerTabs, tab]
  );

  const actionOptions = useMemo(() => CANDIDATE_STATUS_OPTIONS, []);

  const quickActionItems = useMemo(
    () => [
      {
        key: "share",
        label: "Share",
        onClick: runLoggedQuickAction("share", "Share", () => setShareDialogOpen(true)),
        icon: <IosShareOutlinedIcon sx={{ fontSize: 16, color: "#fff" }} />,
      },
      {
        key: "call",
        label: "Call",
        onClick: () => openContactCall(activeCandidate),
        icon: <Call size="18" color="#fff" />,
      },
      {
        key: "email",
        label: "Email",
        onClick: () => openContactEmail(activeCandidate),
        icon: <Sms size="18" color="#fff" />,
      },
      {
        key: "schedule",
        label: "Schedule",
        onClick: () => openInterviewScheduler({ candidateId: activeCandidate?.id, candidate: activeCandidate, jobId: activeCandidate?.job_id || activeCandidate?.job_assignments?.[0]?.id }),
        icon: <Calendar size="18" color="#fff" />,
      },
      {
        key: "add_to_job",
        label: "Add to Job",
        onClick: handleQuickActionAddToJob,
        icon: <Briefcase size="18" color="#fff" />,
      },
      {
        key: "match_to_job",
        label: "Match to Job",
        onClick: runLoggedQuickAction("match_to_job", "Match to Job", () => console.log("Match to Job")),
        icon: <MatchActionIcon size={18} />,
      },
    ],
    [activeCandidate, handleQuickActionAddToJob, runLoggedQuickAction]
  );

  const sidebarStageOptions = useMemo(
    () => Array.from(new Set(sidebarStageRows.map((row) => String(row?.stage || "").trim()).filter(Boolean))),
    [sidebarStageRows]
  );
  const filteredSidebarStageRows = useMemo(
    () =>
      sidebarStageRows.filter(
        (row) => sidebarStageFilter === "all" || String(row?.stage || "").toLowerCase() === sidebarStageFilter
      ),
    [sidebarStageFilter, sidebarStageRows]
  );
  const sidebarStagePageCount = Math.max(1, Math.ceil(filteredSidebarStageRows.length / 2));
  const visibleSidebarStageRows = useMemo(
    () => filteredSidebarStageRows.slice(sidebarStagePage * 2, sidebarStagePage * 2 + 2),
    [filteredSidebarStageRows, sidebarStagePage]
  );
  const sidebarStageItems = useMemo(
    () =>
      visibleSidebarStageRows.map((row) => ({
        id: row?.id || `${row?.job_id || "job"}:${row?.stage || "stage"}`,
        dateText: formatSidebarStageDate(row?.updated_at || row?.created_at),
        badge: row?.stage ? { text: row.stage } : null,
        stage: row?.stage || "New",
        job: row?.job_title || "Unassigned job",
        company: row?.company_name || "—",
        hiringManager: row?.hiring_manager_name || row?.contact_name || "—",
        teamMember: row?.recruiter_name || row?.owner || "—",
        jobId: row?.job_id,
      })),
    [visibleSidebarStageRows]
  );
  const sidebarJobMatchItems = useMemo(
    () =>
      [...sidebarStageRows]
        .sort((left, right) => Number(right?.match_score ?? -1) - Number(left?.match_score ?? -1))
        .slice(0, 6)
        .map((row) => ({
          id: row?.id || row?.job_id,
          title: row?.job_title || "Untitled job",
          company: row?.company_name || "—",
          match: Number.isFinite(Number(row?.match_score)) ? Math.round(Number(row.match_score)) : "—",
          applied: String(row?.stage || "").toLowerCase() === "applied",
          jobId: row?.job_id,
        })),
    [sidebarStageRows]
  );
  const openSidebarJob = useCallback(
    (item) => {
      const jobId = Number(item?.jobId || 0);
      if (Number.isFinite(jobId) && jobId > 0) {
        navigate(`/jobs?jobId=${encodeURIComponent(String(jobId))}&tab=details`);
      }
    },
    [navigate]
  );

  if (!activeCandidate) return null;

  return (
    <>
      <ListTableTabsTemplate
      rootSx={{ pb: 3 }}
      shellSx={{ pb: 0 }}
      breadcrumb={
        <Breadcrumb
          activeLabel={activeBreadcrumbLabel}
          items={[
            { label: "Overview", onClick: () => navigate("/overview") },
            // Let the parent close the active profile before navigation so a
            // cached route snapshot cannot reopen it with stale data.
            ...(typeof onBack === "function"
              ? [{ label: "Candidates", onClick: onBack }]
              : [{ label: "Candidates", path: "/candidates" }]),
            { label: candidateName },
          ]}
        />
      }
      statsSection={
        <StatsSlider
          items={stats}
          autoPlay={false}
          showArrows={true}
          cardMinWidth={200}
          gap={12}
          onItemClick={handleStatItemClick}
          activeItemId={tab === "activities" ? "activities" : null}
        />
      }
      headerSection={
        <CompanyProfileHeader
          company={activeCandidate}
          summaryRow={
            <CompanyHeader
              company={activeCandidate}
              subtitle={[
                activeCandidate?.title || activeCandidate?.candidate_title,
                activeCandidate?.company ||
                  activeCandidate?.candidate_company ||
                  activeCandidate?.current_company ||
                  activeCandidate?.company_client ||
                  activeCandidate?.company_name,
              ].map((value) => String(value || "").trim()).filter(Boolean).join(" · ")}
              leadId={candidateId}
              sx={{ mt: 0.15, mb: 0 }}
            />
          }
          title={
            candidateName && candidateName !== "Candidate"
              ? `Candidate Profile: ${candidateName}`
              : "Candidate Profile"
          }
          tab={tab}
          onTabChange={setTab}
          tabs={headerTabs}
          counts={tabCounts}
          metaLabel="Recruiter"
          metaValue={recruiterMetaValue}
          dateText={dateText}
          showAction={true}
          actionLabel="Status"
          newAction={selectedStatus}
          onNewActionChange={handleStatusChange}
          actionPlaceholder="Select Status"
          actionShowPlaceholderOption={false}
          actionOptions={actionOptions}
          actionDisabled={statusSaving || !candidateId}
          actionFeedback={statusToast}
          onActionFeedbackClose={() =>
            setStatusToast((prev) => ({ ...prev, open: false }))
          }
          sx={TAB_VIEW_HEADER_SX}
        />
      }
      contentSection={
        <>
          <CandidateRecruitingActions candidate={{ ...activeCandidate, job_assignments: sidebarStageRows.map(row => ({
            id: row.job_id, title: row.job_title, stage: row.stage,
          })) }} />
          {tab === "details" && (
            <CandidateDetailsTabContent
              candidate={activeCandidate}
              fieldOptions={detailFieldOptions}
            />
          )}

          {tab === "stages" && (
            <Box sx={{ p: 0 }}>
              <CandidateStagesTabContent
                candidateId={candidateId}
                refreshKey={stageRefreshKey}
                onStageDataChanged={handleCandidateStageDataChanged}
                recruiterUsersByName={recruiterUsersByName}
              />
            </Box>
          )}

          {tab === "activities" && (
            <Box sx={{ p: 0 }}>
              <CandidateActivitiesTabContent
                activities={activityItems}
                onReadMore={(item) => console.log("read more", item)}
                onEdit={(item) => console.log("edit", item)}
                onDelete={handleDeleteActivity}
              />
            </Box>
          )}
        </>
      }
      contentWrapperSx={TAB_VIEW_CONTENT_WRAPPER_SX}
      rightSidebar={
        tab === "details" ? (
          <>
            <QuickActionsCard actionItems={quickActionItems} />
            <AIAgentCard
              companySeed={aiCompanySeed}
              entityType="candidate"
              entityId={candidateId}
              entityName={candidateName}
            />

            <ActivitiesCard
              items={sidebarActivityItems}
              onViewAll={() => setTab("activities")}
              onView={(item) => console.log("view activity", item)}
              onEmail={() => openContactEmail(activeCandidate)}
              onCall={() => openContactCall(activeCandidate)}
              onMore={(item) => console.log("more activity actions", item)}
            />

            <StagesCard
              items={sidebarStageItems}
              stageOptions={sidebarStageOptions}
              filterValue={sidebarStageFilter}
              resultsText={`${filteredSidebarStageRows.length} result${filteredSidebarStageRows.length === 1 ? "" : "s"}`}
              loading={sidebarStagesLoading}
              onFilterChange={(value) => {
                setSidebarStageFilter(value);
                setSidebarStagePage(0);
              }}
              onPrev={() => setSidebarStagePage((page) => Math.max(0, page - 1))}
              onNext={() => setSidebarStagePage((page) => Math.min(sidebarStagePageCount - 1, page + 1))}
              onViewDetails={openSidebarJob}
              onViewAll={() => setTab("stages")}
            />
            <JobMatchesCard
              items={sidebarJobMatchItems}
              onRowClick={openSidebarJob}
              onViewAll={() => setTab("stages")}
            />
            <HiringTeamCard
              apiBase={API_BASE}
              entityType="candidate"
              entityId={activeCandidate?.candidate_id || activeCandidate?.id}
              source={activeCandidate?.source || ""}
            />
          </>
        ) : tab === "stages" ? (
          <>
            <QuickActionsCard actionItems={quickActionItems} />
            <AIAgentCard
              companySeed={aiCompanySeed}
              entityType="candidate"
              entityId={candidateId}
              entityName={candidateName}
            />
          </>
        ) : tab === "activities" ? (
          <>
            <QuickActionsCard actionItems={quickActionItems} />
            <AIAgentCard
              companySeed={aiCompanySeed}
              entityType="candidate"
              entityId={candidateId}
              entityName={candidateName}
            />

            <StagesCard
              items={sidebarStageItems}
              stageOptions={sidebarStageOptions}
              filterValue={sidebarStageFilter}
              resultsText={`${filteredSidebarStageRows.length} result${filteredSidebarStageRows.length === 1 ? "" : "s"}`}
              loading={sidebarStagesLoading}
              onFilterChange={(value) => {
                setSidebarStageFilter(value);
                setSidebarStagePage(0);
              }}
              onPrev={() => setSidebarStagePage((page) => Math.max(0, page - 1))}
              onNext={() => setSidebarStagePage((page) => Math.min(sidebarStagePageCount - 1, page + 1))}
              onViewDetails={openSidebarJob}
              onViewAll={() => setTab("stages")}
            />
            <JobMatchesCard
              items={sidebarJobMatchItems}
              onRowClick={openSidebarJob}
              onViewAll={() => setTab("stages")}
            />
            <HiringTeamCard
              apiBase={API_BASE}
              entityType="candidate"
              entityId={activeCandidate?.candidate_id || activeCandidate?.id}
              source={activeCandidate?.source || ""}
            />
          </>
        ) : null
      }
        rightWidth={360}
        leftColumnSx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 3,
        }}
      />

      <CandidateShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        candidate={activeCandidate}
      />

      <AddCandidateToJobDialog
        open={addToJobDialogOpen}
        onClose={() => setAddToJobDialogOpen(false)}
        onSubmit={handleQuickActionAddCandidateToJob}
        stageOptions={JOB_CANDIDATE_STAGE_OPTIONS}
        initialStage="New"
        candidateName={candidateName}
      />

    </>
  );
}
